import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { fetchWithAuth } from "../utils/api";

const CANTONS = ["AG", "AI", "AR", "BE", "BL", "BS", "FR", "GE", "GL", "GR", "JU", "LU", "NE", "NW", "OW", "SG", "SH", "SZ", "TG", "TI", "UR", "VD", "VS", "ZG", "ZH"];
const DAY_ORDER = [1, 2, 3, 4, 5, 6, 0]; // Lun → Dim (valeurs getDay())
const DIVE_TYPES = ["loisir", "deco"];
const GASES = ["air", "nx", "tx"];
const CONFIGS = ["oc", "cc"];

export default function AlertPreferences() {
  const { t, i18n } = useTranslation();
  const apiUrl = process.env.REACT_APP_API_URL;

  const [active, setActive] = useState(true);
  const [days, setDays] = useState([]);
  const [cantons, setCantons] = useState([]);
  const [diveTypes, setDiveTypes] = useState([]);
  const [gas, setGas] = useState([]);
  const [config, setConfig] = useState([]);
  const [siteIds, setSiteIds] = useState([]);
  const [sitesList, setSitesList] = useState([]);
  const [siteSearch, setSiteSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [openSection, setOpenSection] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetchWithAuth(`${apiUrl}/notification-preferences`);
        const data = await res.json();
        if (alive && data) {
          setActive(data.active !== false);
          setDays(data.days_of_week || []);
          setCantons(data.cantons || []);
          setDiveTypes(data.dive_types || []);
          setGas(data.gas || []);
          setConfig(data.config || []);
          setSiteIds(data.site_ids || []);
        }
      } catch (e) {
        console.error("Erreur chargement alertes :", e);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [apiUrl]);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetchWithAuth(`${apiUrl}/diving-sites`);
        const data = await res.json();
        setSitesList(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error("Erreur chargement sites :", e);
      }
    })();
  }, [apiUrl]);

  const toggle = (arr, setArr, val) =>
    setArr(arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val]);

  const dayLabel = (d) => {
    const date = new Date(2024, 0, 7 + d); // 2024-01-07 = dimanche → getDay() === d
    const s = date.toLocaleDateString(i18n.language, { weekday: "short" });
    return s.charAt(0).toUpperCase() + s.slice(1);
  };

  const save = async () => {
    try {
      setSaving(true);
      const res = await fetchWithAuth(`${apiUrl}/notification-preferences`, {
        method: "POST",
        body: JSON.stringify({
          active, days_of_week: days, cantons, dive_types: diveTypes, gas, config, site_ids: siteIds,
        }),
      });
      if (!res.ok) throw new Error("save failed");
      toast.success(t('alerts.saved'));
    } catch (e) {
      console.error(e);
      toast.error(t('alerts.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const Chip = ({ selected, onClick, children }) => (
    <button
      type="button"
      onClick={onClick}
      style={{
        border: `1px solid ${selected ? "var(--primary-blue)" : "#cfd8dd"}`,
        background: selected ? "var(--primary-blue)" : "#fff",
        color: selected ? "#fff" : "#55606a",
        borderRadius: 999, padding: "5px 11px", fontSize: 13, fontWeight: 600, cursor: "pointer",
      }}
    >
      {children}
    </button>
  );

  const Section = ({ id, label, summary, children }) => {
    const open = openSection === id;
    return (
      <div style={{ borderTop: "1px solid #eef2f5" }}>
        <button
          type="button"
          onClick={() => setOpenSection(open ? null : id)}
          style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, background: "none", border: "none", padding: "12px 2px", cursor: "pointer", textAlign: "left" }}
        >
          <span style={{ fontWeight: 600, color: "#334", fontSize: 14, flexShrink: 0 }}>{label}</span>
          <span style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
            <span style={{ color: "#8a97a3", fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{summary}</span>
            <span style={{ color: "#8a97a3", fontSize: 12, flexShrink: 0 }}>{open ? "▲" : "▼"}</span>
          </span>
        </button>
        {open && <div style={{ display: "flex", flexWrap: "wrap", gap: 6, padding: "0 2px 14px" }}>{children}</div>}
      </div>
    );
  };

  const any = t('alerts.any');
  const sumDays = days.length ? DAY_ORDER.filter((d) => days.includes(d)).map(dayLabel).join(", ") : any;
  const sumTypes = diveTypes.length ? diveTypes.map((v) => t(`diveTypes.${v}`)).join(", ") : any;
  const sumConfig = config.length ? config.map((v) => t(`configTypes.${v}`)).join(", ") : any;
  const sumGas = gas.length ? gas.map((v) => t(`gasTypes.${v}`)).join(", ") : any;
  const sumCantons = cantons.length ? cantons.join(", ") : any;
  const siteName = (id) => sitesList.find((s) => s.id === id)?.name || "…";
  const sumSites = siteIds.length ? siteIds.map(siteName).join(", ") : any;

  const hasAnyCriteria =
    days.length || cantons.length || diveTypes.length || gas.length || config.length || siteIds.length;

  if (loading) return null;

  return (
    <div style={{ marginBottom: "20px" }}>
      <div style={{ background: "#fff", padding: "15px 20px", borderRadius: 10, boxShadow: "0 2px 5px rgba(0,0,0,0.05)", textAlign: "left" }}>
        <h4 style={{ color: "var(--primary-blue)", marginTop: 0, marginBottom: 4 }}>{t('alerts.title')}</h4>
        <p style={{ color: "#777", fontSize: 13, marginTop: 0, marginBottom: 14 }}>{t('alerts.description')}</p>
        <label style={{ display: "flex", alignItems: "center", gap: 8, padding: "12px 0", cursor: "pointer", fontWeight: 600, color: "#334" }}>
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
          {t('alerts.active')}
        </label>

        {active && !hasAnyCriteria && (
          <div style={{ background: "#fff8e6", border: "1px solid #ffe0a0", color: "#8a6d1a", borderRadius: 8, padding: "8px 12px", fontSize: 13, marginBottom: 8 }}>
            {t('alerts.needCriteria')}
          </div>
        )}

        <div style={{ opacity: active ? 1 : 0.45, pointerEvents: active ? "auto" : "none" }}>
          <Section id="sites" label={t('alerts.sites')} summary={sumSites}>
            {siteIds.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, width: "100%" }}>
                {siteIds.map((id) => (
                  <Chip key={id} selected onClick={() => toggle(siteIds, setSiteIds, id)}>{siteName(id)} ✕</Chip>
                ))}
              </div>
            )}
            <input
              type="text"
              placeholder={t('alerts.searchSite')}
              value={siteSearch}
              onChange={(e) => setSiteSearch(e.target.value)}
              style={{ width: "100%", padding: "7px 10px", border: "1px solid #ccc", borderRadius: 8, boxSizing: "border-box", fontSize: 13 }}
            />
            <div style={{ width: "100%", maxHeight: 180, overflowY: "auto", border: "1px solid #eef2f5", borderRadius: 8 }}>
              {sitesList
                .filter((s) => !siteIds.includes(s.id) && (!siteSearch.trim() || (s.name || "").toLowerCase().includes(siteSearch.trim().toLowerCase())))
                .slice(0, 50)
                .map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => toggle(siteIds, setSiteIds, s.id)}
                    style={{ width: "100%", textAlign: "left", background: "none", border: "none", borderBottom: "1px solid #f0f0f0", padding: "8px 10px", cursor: "pointer", fontSize: 13, display: "flex", justifyContent: "space-between", gap: 8 }}
                  >
                    <span>{s.name}</span>
                    {s.canton && <span style={{ color: "#8aa0a8", flexShrink: 0 }}>{s.canton}</span>}
                  </button>
                ))}
            </div>
          </Section>
          <Section id="cantons" label={t('alerts.cantons')} summary={sumCantons}>
            {CANTONS.map((c) => <Chip key={c} selected={cantons.includes(c)} onClick={() => toggle(cantons, setCantons, c)}>{c}</Chip>)}
          </Section>
          <Section id="days" label={t('alerts.days')} summary={sumDays}>
            {DAY_ORDER.map((d) => <Chip key={d} selected={days.includes(d)} onClick={() => toggle(days, setDays, d)}>{dayLabel(d)}</Chip>)}
          </Section>
          <Section id="type" label={t('alerts.diveType')} summary={sumTypes}>
            {DIVE_TYPES.map((v) => <Chip key={v} selected={diveTypes.includes(v)} onClick={() => toggle(diveTypes, setDiveTypes, v)}>{t(`diveTypes.${v}`)}</Chip>)}
          </Section>
          <Section id="config" label={t('alerts.config')} summary={sumConfig}>
            {CONFIGS.map((v) => <Chip key={v} selected={config.includes(v)} onClick={() => toggle(config, setConfig, v)}>{t(`configTypes.${v}`)}</Chip>)}
          </Section>
          <Section id="gas" label={t('alerts.gas')} summary={sumGas}>
            {GASES.map((v) => <Chip key={v} selected={gas.includes(v)} onClick={() => toggle(gas, setGas, v)}>{t(`gasTypes.${v}`)}</Chip>)}
          </Section>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
          <button
            onClick={save}
            disabled={saving}
            style={{
              background: "var(--primary-blue)", color: "#fff", border: "none", borderRadius: 6,
              padding: "8px 16px", fontWeight: 700, fontSize: 14, cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.6 : 1,
            }}
          >
            {saving ? t('profile.buttons.saving') : t('common.save')}
          </button>
        </div>
      </div>
    </div>
  );
}
